import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { formatBlogPublishDate, getBlogPublishDate, getBlogPublishTimestamp } from '../utils/blogPublishDate';
import { useTheme } from '../contexts/ThemeContext';
import SEOHelmet from './SEOHelmet';
import './BlogsPage.css';

const BLOGS_PER_PAGE = 9;

const getBlogSlug = (title = '') => title
  .toLowerCase()
  .replace(/[^a-z0-9 -]/g, '')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-')
  .trim('-');

const formatBlogDate = (blog) => formatBlogPublishDate(blog);

const BlogsPage = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blogImages, setBlogImages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const requestedImages = useRef(new Set());
  const journalRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchBlogs = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await fetch(
          `https://tophamtechbe.com/api/treas/blogs/v1/get-blog?site_id=${SITE_CONFIG.SITE_ID}`,
          {
            headers: { 'X-API-Key': 'treas-api-key-2025' },
            signal: controller.signal
          }
        );

        if (!response.ok) throw new Error(`Blog request failed with ${response.status}`);

        const data = await response.json();
        const now = Date.now();
        const visibleBlogs = (Array.isArray(data) ? data : [])
          .filter((blog) => !blog.future_post_date || new Date(blog.future_post_date).getTime() <= now)
          .sort((a, b) => getBlogPublishTimestamp(b) - getBlogPublishTimestamp(a));

        setBlogs(visibleBlogs);
        setCurrentPage(1);
      } catch (fetchError) {
        if (fetchError.name !== 'AbortError') {
          console.error('Error fetching blogs:', fetchError);
          setError(true);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchBlogs();
    return () => controller.abort();
  }, [reloadKey]);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const pageCount = Math.max(1, Math.ceil(blogs.length / BLOGS_PER_PAGE));
  const pageStart = (currentPage - 1) * BLOGS_PER_PAGE;
  const paginatedBlogs = useMemo(
    () => blogs.slice(pageStart, pageStart + BLOGS_PER_PAGE),
    [blogs, pageStart]
  );

  useEffect(() => {
    const controller = new AbortController();
    const imageRequestCache = requestedImages.current;
    const blogsNeedingImages = paginatedBlogs.filter(
      (blog) => blog.file_id && !blogImages[blog.id] && !imageRequestCache.has(blog.id)
    );

    if (blogsNeedingImages.length === 0) return () => controller.abort();
    blogsNeedingImages.forEach((blog) => imageRequestCache.add(blog.id));

    const fetchPageImages = async () => {
      const imageEntries = await Promise.all(blogsNeedingImages.map(async (blog) => {
        try {
          const response = await fetch(
            `https://tophamtechbe.com/api/treas/blogs/v1/get-image?site_id=${SITE_CONFIG.SITE_ID}&s3_blog_file_id=${blog.file_id}`,
            {
              headers: { 'X-API-Key': 'treas-api-key-2025' },
              signal: controller.signal
            }
          );

          if (!response.ok) return null;
          const imageData = await response.json();
          return imageData.url ? [blog.id, imageData.url] : null;
        } catch (imageError) {
          if (imageError.name !== 'AbortError') {
            console.error('Error fetching image for blog:', blog.id, imageError);
          }
          return null;
        }
      }));

      if (!controller.signal.aborted) {
        const loadedImages = Object.fromEntries(imageEntries.filter(Boolean));
        if (Object.keys(loadedImages).length > 0) {
          setBlogImages((previous) => ({ ...previous, ...loadedImages }));
        }
      }
    };

    fetchPageImages();
    return () => {
      controller.abort();
      blogsNeedingImages.forEach((blog) => imageRequestCache.delete(blog.id));
    };
  }, [paginatedBlogs, blogImages]);

  const openBlog = (blog) => navigate(`/blog/${getBlogSlug(blog.title) || blog.id}`);

  const changePage = (nextPage) => {
    setCurrentPage(nextPage);
    journalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={`blogs-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      <SEOHelmet
        title={`Inland Empire Real Estate Journal | ${SITE_CONFIG.AGENT.NAME}`}
        description={`Local market insight, home guidance, and Southern California real estate stories from ${SITE_CONFIG.AGENT.NAME}.`}
        keywords="Inland Empire real estate blog, Upland homes, home buying guide, market insights, home selling tips"
        canonical={`https://${SITE_CONFIG.BASE_URL}/blogs`}
      />

      <header className="blogs-site-header">
        <button className="blogs-brand" type="button" onClick={() => navigate('/')} aria-label="Return home">
          <img src={SITE_CONFIG.BRAND.LOGO_URL} alt={`${SITE_CONFIG.AGENT.NAME} logo`} />
        </button>

        <nav className="blogs-nav" aria-label="Journal navigation">
          <button type="button" onClick={() => navigate('/search')}>Search Homes</button>
          <button type="button" onClick={toggleTheme} aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} theme`}>
            {isDarkMode ? 'Light' : 'Dark'} Mode
          </button>
          <button className="blogs-home-button" type="button" onClick={() => navigate('/')}>Home <span aria-hidden="true">↗</span></button>
        </nav>
      </header>

      <main>
        <section className="blogs-hero" aria-labelledby="blogs-title">
          <div className="blogs-hero-glow" aria-hidden="true"></div>
          <div className="blogs-hero-content">
            <span className="blogs-eyebrow">Inland Empire · The Real Estate Journal</span>
            <h1 id="blogs-title">Local perspective.<br /><em>Thoughtfully shared.</em></h1>
            <p>Market intelligence, neighborhood stories, and considered guidance for making your next move with confidence.</p>
          </div>
          <div className="blogs-hero-aside" aria-hidden="true">
            <span>{String(blogs.length).padStart(2, '0')}</span>
            <p>Stories and insights<br />from Southern California</p>
          </div>
          <span className="blogs-hero-wordmark" aria-hidden="true">Journal</span>
        </section>

        <section className="blogs-journal" ref={journalRef} aria-labelledby="latest-stories-title" aria-busy={loading}>
          <div className="blogs-section-heading">
            <div>
              <span>Knowledge for the journey</span>
              <h2 id="latest-stories-title">Latest stories</h2>
            </div>
            {!loading && blogs.length > 0 && (
              <p>Showing {pageStart + 1}–{Math.min(pageStart + BLOGS_PER_PAGE, blogs.length)} of {blogs.length}</p>
            )}
          </div>

          {loading && (
            <div className="blogs-grid" aria-label="Loading journal stories">
              {Array.from({ length: BLOGS_PER_PAGE }).map((_, index) => (
                <div className={`blog-card blog-card--skeleton ${index === 0 ? 'blog-card--featured' : ''}`} key={index}>
                  <span className="blog-skeleton-media"></span>
                  <span className="blog-skeleton-line blog-skeleton-line--short"></span>
                  <span className="blog-skeleton-line"></span>
                  <span className="blog-skeleton-line"></span>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="blogs-state-message" role="alert">
              <span>Connection interrupted</span>
              <h2>We couldn’t load the journal.</h2>
              <p>Please try again and we’ll reconnect you with the latest stories.</p>
              <button type="button" onClick={() => setReloadKey((key) => key + 1)}>Try Again</button>
            </div>
          )}

          {!loading && !error && blogs.length === 0 && (
            <div className="blogs-state-message">
              <span>The journal</span>
              <h2>New stories are on the way.</h2>
              <p>Check back soon for fresh Inland Empire market insight.</p>
            </div>
          )}

          {!loading && !error && paginatedBlogs.length > 0 && (
            <div className="blogs-grid">
              {paginatedBlogs.map((blog, index) => {
                const imageUrl = blogImages[blog.id];
                const isFeatured = index === 0;

                return (
                  <article className={`blog-card ${isFeatured ? 'blog-card--featured' : ''}`} key={blog.id || `${blog.title}-${index}`}>
                    <button className="blog-card-link" type="button" onClick={() => openBlog(blog)} aria-label={`Read ${blog.title}`}>
                      <span className={`blog-card-media ${imageUrl ? 'is-loaded' : ''}`}>
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt=""
                            loading={isFeatured ? 'eager' : 'lazy'}
                            fetchPriority={isFeatured ? 'high' : 'auto'}
                            decoding="async"
                          />
                        ) : (
                          <span className="blog-card-placeholder" aria-hidden="true">
                            <strong>{SITE_CONFIG.AGENT.NAME.split(' ').map((word) => word[0]).join('')}</strong>
                          </span>
                        )}
                        <span className="blog-card-number" aria-hidden="true">{String(pageStart + index + 1).padStart(2, '0')}</span>
                      </span>

                      <span className="blog-card-body">
                        <span className="blog-card-meta">
                          <time dateTime={getBlogPublishDate(blog)}>{formatBlogDate(blog)}</time>
                          <span>Real Estate</span>
                        </span>
                        <h3>{blog.title}</h3>
                        {blog.meta_description && <p>{blog.meta_description}</p>}
                        <span className="blog-card-cta">Read the story <span aria-hidden="true">→</span></span>
                      </span>
                    </button>
                  </article>
                );
              })}
            </div>
          )}

          {!loading && !error && pageCount > 1 && (
            <nav className="blogs-pagination" aria-label="Journal pages">
              <button type="button" disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)}>
                <span aria-hidden="true">←</span> Newer
              </button>
              <span aria-live="polite"><strong>{String(currentPage).padStart(2, '0')}</strong> / {String(pageCount).padStart(2, '0')}</span>
              <button type="button" disabled={currentPage === pageCount} onClick={() => changePage(currentPage + 1)}>
                Older <span aria-hidden="true">→</span>
              </button>
            </nav>
          )}
        </section>
      </main>

      <footer className="blogs-footer">
        <div>
          <span>Ready for your next chapter?</span>
          <h2>Let’s make your Southern California move feel informed.</h2>
        </div>
        <button type="button" onClick={() => navigate('/contact-us')}>Work With {SITE_CONFIG.AGENT.NAME.split(' ')[0]} <span aria-hidden="true">→</span></button>
      </footer>
    </div>
  );
};

export default BlogsPage;
